import { useEffect, useState } from "react";
import { api, fmtCurrency } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus, CalendarDays } from "lucide-react";

const EMPLOYMENT_TYPES = [
  { v: "festanstellung", l: "Festanstellung (AG-Faktor ~21%)" },
  { v: "freelancer", l: "Freelancer / Honorarkraft" },
  { v: "werkstudent", l: "Werkstudent (UV-Faktor ~5%)" },
  { v: "minijob", l: "Minijob (Pauschal ~30%)" },
];

export default function HiringPage() {
  const [form, setForm] = useState({ employment_type: "festanstellung", gross_monthly: 4000, duration_months: 12, expected_additional_monthly_revenue: 6000, ag_factor_override: "" });
  const [result, setResult] = useState(null);
  const [calendar, setCalendar] = useState(null);
  const [loading, setLoading] = useState(false);
  const year = new Date().getFullYear();

  useEffect(() => { api.get(`/payroll/calendar?year=${year}`).then((r) => setCalendar(r.data)); }, [year]);

  const simulate = async () => {
    setLoading(true);
    try {
      const payload = {
        employment_type: form.employment_type,
        gross_monthly: Number(form.gross_monthly),
        duration_months: Number(form.duration_months),
        expected_additional_monthly_revenue: Number(form.expected_additional_monthly_revenue) || 0,
        ag_factor_override: form.ag_factor_override ? Number(form.ag_factor_override) : null,
      };
      const { data } = await api.post("/hiring/simulate", payload);
      setResult(data);
    } catch (e) { /* ignore */ }
    setLoading(false);
  };

  return (
    <div className="space-y-6" data-testid="hiring-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold">Cashflow-Steuerung</div>
        <h1 className="font-display text-3xl font-semibold tracking-tight mt-1 flex items-center gap-3">
          <UserPlus className="h-7 w-7" /> Hiring &amp; Personal-Cashflow
        </h1>
        <p className="text-sm text-neutral-500 mt-2">
          Simuliere die Auswirkung einer Neueinstellung auf deinen Cashflow. AgencyCash kalkuliert mit AG-Anteil, Break-even und Liquiditätsreichweite vor und nach Hiring.
        </p>
      </div>

      <div className="rounded-xl border border-neutral-200 bg-white p-6">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="sm:col-span-2">
            <Label>Anstellungsart</Label>
            <Select value={form.employment_type} onValueChange={(v) => setForm({ ...form, employment_type: v })}>
              <SelectTrigger data-testid="hire-type"><SelectValue /></SelectTrigger>
              <SelectContent>{EMPLOYMENT_TYPES.map(t => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Bruttogehalt / Monat (€)</Label>
            <Input type="number" value={form.gross_monthly} onChange={(e) => setForm({ ...form, gross_monthly: e.target.value })} data-testid="hire-gross" />
          </div>
          <div>
            <Label>Geplante Dauer (Monate)</Label>
            <Input type="number" value={form.duration_months} onChange={(e) => setForm({ ...form, duration_months: e.target.value })} data-testid="hire-duration" />
          </div>
          <div>
            <Label>Erwarteter Zusatzumsatz / Monat (€)</Label>
            <Input type="number" value={form.expected_additional_monthly_revenue} onChange={(e) => setForm({ ...form, expected_additional_monthly_revenue: e.target.value })} data-testid="hire-revenue" />
          </div>
          <div>
            <Label>AG-Faktor überschreiben (optional)</Label>
            <Input type="number" step="0.01" placeholder="z.B. 1.25" value={form.ag_factor_override} onChange={(e) => setForm({ ...form, ag_factor_override: e.target.value })} data-testid="hire-factor" />
          </div>
          <div className="flex items-end">
            <Button onClick={simulate} disabled={loading} className="w-full" data-testid="hire-simulate">{loading ? "…" : "Simulieren"}</Button>
          </div>
        </div>
      </div>

      {result && (
        <div className="rounded-xl border-2 p-6" style={{ borderColor: result.traffic_light === "red" ? "#FCA5A5" : result.traffic_light === "yellow" ? "#FCD34D" : "#86EFAC", backgroundColor: result.traffic_light === "red" ? "#FEF2F2" : result.traffic_light === "yellow" ? "#FFFBEB" : "#F0FDF4" }} data-testid="hire-result">
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold mb-2">Empfehlung</div>
          <div className="font-display text-2xl font-semibold mb-2" data-testid="hire-recommendation">{result.recommendation}</div>
          <p className="text-sm text-neutral-700 mb-6">
            Monatliche Gesamtkosten: <strong>{fmtCurrency(result.monthly_cost)}</strong> (Faktor {result.ag_factor})
            {" · "}Gesamt über {result.duration_months} Monate: <strong>{fmtCurrency(result.total_cost)}</strong>
            {result.expected_additional_monthly_revenue > 0 && <> {" · "} Erwarteter Zusatzumsatz: <strong>{fmtCurrency(result.total_expected_revenue)}</strong></>}
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card label="Kontostand vorher" value={fmtCurrency(result.balance_before)} />
            <Card label="Kontostand nach (ohne Umsatz)" value={fmtCurrency(result.balance_after_no_rev)} severity={result.balance_after_no_rev < 0 ? "red" : "neutral"} />
            <Card label="Kontostand nach (mit Umsatz)" value={fmtCurrency(result.balance_after_with_rev)} severity="green" />
            <Card label="Monatliches Netto-Δ" value={fmtCurrency(result.monthly_net)} severity={result.monthly_net >= 0 ? "green" : "red"} />
            <Card label="Fixkosten vorher" value={fmtCurrency(result.monthly_fixed_before)} />
            <Card label="Fixkosten nach Hiring" value={fmtCurrency(result.monthly_fixed_after)} severity="yellow" />
            <Card label="Runway ohne Umsatz" value={result.runway_after_no_rev_months == null ? "∞" : `${result.runway_after_no_rev_months} M`} severity={result.runway_after_no_rev_months !== null && result.runway_after_no_rev_months < 3 ? "red" : "neutral"} />
            <Card label="Runway mit Umsatz" value={result.runway_after_with_rev_months == null ? "∞" : `${result.runway_after_with_rev_months} M`} severity="green" />
          </div>
        </div>
      )}

      {calendar && (
        <div className="space-y-3" data-testid="payroll-calendar">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-neutral-500 font-semibold">
            <CalendarDays className="h-4 w-4" /> Payroll-Kalender {calendar.year} · {calendar.headcount} Personalposten · Jahressumme {fmtCurrency(calendar.grand_total)}
          </div>
          <div className="rounded-xl border border-neutral-200 bg-white p-4 grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3">
            {calendar.months.map((m) => (
              <div key={m.month} className="rounded-lg bg-neutral-50 p-3" data-testid={`payroll-month-${m.month}`}>
                <div className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">{m.label}</div>
                <div className="font-display text-base font-semibold mt-1 tabular-nums">{fmtCurrency(m.total_gross)}</div>
                <div className="text-[10px] text-neutral-500 mt-1">{m.entries.length} Posten</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Card({ label, value, severity }) {
  const color = severity === "red" ? "text-red-700" : severity === "green" ? "text-emerald-700" : severity === "yellow" ? "text-amber-700" : "text-neutral-900";
  return (
    <div className="rounded-lg bg-white border border-neutral-200 p-4">
      <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 font-semibold">{label}</div>
      <div className={`font-display text-lg font-semibold mt-2 tabular-nums ${color}`}>{value}</div>
    </div>
  );
}
